import React, { useContext, useEffect, useState } from 'react';
import {
  Field,
  RichText,
  SitecoreContext,
  ComponentRendering,
  ComponentParams,
  ComponentFactory,
} from '@sitecore-jss/sitecore-jss-react';

import './index.scss';
import '../../assets/styles/_buttons.scss';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { Checkbox } from '@mui/material';
import CustomTooltip from '../CustomTooltip/CustomTooltip';
import { postAPICall } from '../../dataFetcher/axiosMethodCalls';
import { getCurrencyInUSD } from '../../utils/helper';
import { APIWebstoreContext } from '../../context/globalContext';
import LoadingSkeleton from '../LoadingSkeleton/LoadingSkeleton';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { STATUS_OK, STATUS_REDIRECT } from '../../utils/constants';
import SubmitPaymentProcess from '../SubmitPaymentProcess/SubmitPaymentProcess';
import { UnifiedPaymentDataService } from '../../utils/UnifiedPaymentDataService';

export type UnifiedPaymentInvoiceSummaryProps = {
  fields: {
    MemberDiscountTooltipText: Field<string>;
    TermsAgreementMessage: Field<string>;
    TermsAgreementTooltipText: Field<string>;
    MemberDiscountTitle: Field<string>;
    TaxExemptTooltipText: Field<string>;
    TermsAgreementNoEula: Field<string>;
    SubmitPaymentButtonText: Field<string>;
    ContinueCheckoutButtonText: Field<string>;
    SubscriptionText: Field<string>;
    SingleStandards: Field<number>;
    MultipleStandards: Field<number>;
    NavigateTo: Field<string>;
    LOBSpecificConfiguration: Array<InvoiceSummaryLOBFieldProps>;
    ToastMessage: Field<string>;
    ToastMessagePayFabric: Field<string>;
    PaymentAttemptText: Field<string>;
  };
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
  invoiceIdentifier: string;
};

export type InvoiceSummaryLOBFieldProps = {
  displayName: string;
  fields: {
    ShowAccordion: Field<boolean>;
    ShowShipping: Field<boolean>;
    ShowSalesTax: Field<boolean>;
    ShowMemberDiscount: Field<boolean>;
    ShowEula: Field<boolean>;
    ShowTaxExempt: Field<boolean>;
    LOBIdentifier: Field<string>;
    SectionTitle: Field<string>;
  };
};

interface Props {
  children: React.ReactElement;
  componentFactory: ComponentFactory;
  rendering: ComponentRendering;
  params: ComponentParams;
}

export interface UnifiedPaymentInvoiceSummaryDetails {
  orderId: string;
  subTotal: number;
  memberDiscount: number;
  total: number;
  taxTotal: number;
  shippingTotal: number;
}

export interface IUnifiedPaymentInvoiceSummaryConfigurationInfo {
  ShowAccordion: boolean;
  ShowShipping: boolean;
  ShowSalesTax: boolean;
  ShowMemberDiscount: boolean;
}

export const UnifiedPaymentInvoiceSummary = (
  props: UnifiedPaymentInvoiceSummaryProps & Props
): JSX.Element => {
  const { fields } = props;
  const { state } = useContext(APIWebstoreContext);
  const [invoiceSummaryDetails, setInvoiceSummaryDetails] = useState<{
    [lob: string]: UnifiedPaymentInvoiceSummaryDetails;
  }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isEULA, setIsEULA] = useState(false);
  const [expandedLob, setExpandedLob] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [unpaidInvoices, setUnpaidInvoices] = useState<string[]>([]);

  function getLOBConfig(lob: string): InvoiceSummaryLOBFieldProps | undefined {
    return fields.LOBSpecificConfiguration.find((x) => x.fields.LOBIdentifier.value === lob);
  }

  function getInvoiceSummaryConfiguration(
    lob: string
  ): IUnifiedPaymentInvoiceSummaryConfigurationInfo | undefined {
    const lobSpecificConfig = getLOBConfig(lob);
    if (!lobSpecificConfig) return undefined;

    return {
      ShowAccordion: lobSpecificConfig?.fields?.ShowAccordion?.value,
      ShowShipping: lobSpecificConfig?.fields?.ShowShipping?.value,
      ShowSalesTax: lobSpecificConfig?.fields?.ShowSalesTax?.value,
      ShowMemberDiscount: lobSpecificConfig.fields?.ShowMemberDiscount?.value,
    };
  }

  const fetchData = async () => {
    try {
      const rawQuery = window.location.search;
      const decodedQueryString = decodeURIComponent(rawQuery.substring(1));
      const queryParams = new URLSearchParams(decodedQueryString);
      const groupIdentifier = queryParams.get('GroupNumber') || '';
      const unpaidInvoices = (queryParams.get('UnpaidInvoices') || '')
        .split(',')
        .map((item) => item.trim());

      const payload = { GroupNumber: groupIdentifier, UnpaidInvoices: unpaidInvoices };
      const apiUrl = `${process.env.REACT_APP_CART_URL}/checkout/custominvoicesummary`;

      const response = await postAPICall(apiUrl, payload);

      if (response && response.status >= STATUS_OK && response.status < STATUS_REDIRECT) {
        const data = response?.data;

        if (!data || !data.summary || Object.keys(data.summary).length === 0) {
          throw new Error('No valid summary data found in the response.');
        }

        const newInvoiceSummaryDetails: { [lob: string]: UnifiedPaymentInvoiceSummaryDetails } = {};

        Object.keys(data.summary).forEach((lob) => {
          const orderDetails = data.summary[lob];
          if (orderDetails) {
            newInvoiceSummaryDetails[lob] = {
              orderId: orderDetails.orderIds.join(', '),
              subTotal: orderDetails.subTotal,
              memberDiscount: orderDetails.memberDiscount,
              total: orderDetails.total,
              taxTotal: orderDetails.taxTotal,
              shippingTotal: orderDetails.shippingTotal,
            };
          }
        });

        setInvoiceSummaryDetails(newInvoiceSummaryDetails);

        Object.entries(newInvoiceSummaryDetails).forEach(([lob, summary]) => {
          UnifiedPaymentDataService.setInvoiceSummary(lob, summary);
        });

        setUnpaidInvoices(unpaidInvoices);

        const Total = Object.values(newInvoiceSummaryDetails).reduce(
          (acc, val) => acc + (val?.total || 0),
          0
        );
        setTotal(Total);
      } else {
        throw new Error(`Error: ${response?.status} - ${response?.statusText}`);
      }
    } catch (error) {
      console.error('Error fetching invoice summary:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [state?.toggleInvoiceSummaryLoad]);

  function renderWithLoading(value: any, theme?: any) {
    return isLoading ? <LoadingSkeleton theme={theme} /> : value;
  }

  function renderInvoiceSummaryRow(
    label: string,
    amount: number | string,
    mainClass = 'orderSubTotal'
  ) {
    const displayValue =
      typeof amount === 'number' ? renderWithLoading(getCurrencyInUSD(amount), 'dark') : amount;

    return (
      <div className={mainClass}>
        <p className={`${mainClass}Detail`}>{label}</p>
        <p className={`${mainClass}Detail`} style={{ textAlign: 'right' }}>
          {displayValue}
        </p>
      </div>
    );
  }

  function getInvoiceSummary() {
    const showEULABlock = fields.LOBSpecificConfiguration.some(
      (lobConfig) => lobConfig?.fields?.ShowEula?.value
    );

    if ((state?.invoiceCounts?.totalCount ?? 0) > 0) {
      return (
        <div className="unifiedpaymentinvoicesummary">
          <h1>Invoice Summary</h1>
          {Object.keys(invoiceSummaryDetails).map((lob) => {
            const lobConfig = fields?.LOBSpecificConfiguration?.find(
              (x) => x.fields.LOBIdentifier.value === lob
            );
            if (!lobConfig) return null;
            const isExpanded = expandedLob === lob;
            const orderSummaryConfig = getInvoiceSummaryConfiguration(lob);

            return (
              <React.Fragment key={lob}>
                {orderSummaryConfig?.ShowAccordion ? (
                  <Accordion
                    className="order-summary-accordion"
                    expanded={isExpanded}
                    onChange={() => setExpandedLob((prev) => (prev === lob ? null : lob))}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      id={`${lob}-header`}
                      className="order-summary-standards-collapsed"
                    >
                      <div className="accordionHeader">
                        <p className="accordion-title">{lobConfig?.fields?.SectionTitle?.value}</p>
                        {!isExpanded && (
                          <div className="orderPriceDetails">
                            {renderInvoiceSummaryRow('Invoice Total:', total)}
                          </div>
                        )}
                      </div>
                    </AccordionSummary>
                    <hr className="accordion-line" />
                    <AccordionDetails className="accordion-details order-summary-standards-expanded">
                      <div className="orderPriceDetails">
                        {renderInvoiceSummaryRow('Invoice Subtotal:', total)}
                        {orderSummaryConfig?.ShowMemberDiscount &&
                          invoiceSummaryDetails?.[lob]?.memberDiscount && (
                            <div className="orderMemberDiscount orderSummaryAlign">
                              <div className="orderMemberDiscountTitleSection">
                                <p>Member Discount (30%):</p>
                                <CustomTooltip
                                  fields={{ 'Tooltip Text': fields?.MemberDiscountTooltipText }}
                                />
                              </div>
                              <p className="member-discount-value">
                                {renderWithLoading(
                                  getCurrencyInUSD(invoiceSummaryDetails?.[lob]?.memberDiscount),
                                  'light'
                                )}
                              </p>
                            </div>
                          )}
                        {orderSummaryConfig?.ShowShipping &&
                          renderInvoiceSummaryRow(
                            'Shipping and Handling:',
                            'Calculated at Checkout',
                            'orderShippingAndHandling'
                          )}
                        {orderSummaryConfig?.ShowSalesTax &&
                          renderInvoiceSummaryRow(
                            'Sales Tax:',
                            'Calculated at checkout',
                            'orderSalesTax'
                          )}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                ) : (
                  <Accordion className="order-summary-accordion" expanded={false}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon style={{ visibility: 'hidden' }} />}
                      className="order-summary-standards-collapsed "
                    >
                      <div className="accordionHeader">
                        <p className="accordion-title">{lobConfig?.fields?.SectionTitle?.value}</p>
                        <div className="orderPriceDetails">
                          {renderInvoiceSummaryRow('Invoice Total:', total)}
                        </div>
                      </div>
                    </AccordionSummary>
                  </Accordion>
                )}
              </React.Fragment>
            );
          })}

          <div className="orderAgreementSection">
            <div className="orderAgreementSectionTitleSection">
              <Checkbox
                color="default"
                title={fields?.TermsAgreementTooltipText?.value}
                onChange={(e) => {
                  setIsEULA(e.target.checked);
                }}
              />
            </div>
            <RichText
              className="orderAgreementText"
              field={showEULABlock ? fields?.TermsAgreementMessage : fields?.TermsAgreementNoEula}
            />
          </div>
          <SubmitPaymentProcess
            fields={{
              ToastMessage: fields?.ToastMessage,
              ToastMessagePayFabric: fields?.ToastMessagePayFabric,
              PaymentAttemptText: fields?.PaymentAttemptText,
            }}
            isEULA={isEULA}
            isOrderLoading={isLoading}
            orderTotal={total}
            orderIds={unpaidInvoices}
            invoiceIdentifier={props?.invoiceIdentifier}
            language={state?.groupAdminCountry === 'CN' ? 'zh' : 'en'}
            submitButtonText={
              state?.groupAdminCountry === 'CN'
                ? fields?.ContinueCheckoutButtonText?.value
                : fields?.SubmitPaymentButtonText?.value
            }
          />

          <br />
        </div>
      );
    } else {
      return <div className="unifiedpaymentinvoicesummary"></div>;
    }
  }

  return <div className="unifiedpaymentinvoicesummarySticky">{getInvoiceSummary()}</div>;
};

export default UnifiedPaymentInvoiceSummary;
